// MOD-BOX-OPEN и MOD-ODDS (E05-T08, ux/03 §8–9, ADR-0015). Что выпало, решает только сервер (POST /boxes/open);
// окно показывает ответ. Ключ идемпотентности открытия живёт в памяти устройства до показа результата: если
// связь оборвалась на открытии, следующий вход повторяет тот же запрос и сервер отдаёт уже выданное.
// MOD-ODDS — единое окно шансов для ящиков и добычи с босса: те же строки, что `GET /boxes/{id}/odds`, с версией
// таблицы; офлайн — из кэша с пометкой «Данные на {дата}».
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { RARITY_NAMES, bossInfo, itemName } from '../content/catalog.js';
import { CURRENCY, amount } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { inventory, online, saveCache, setInventoryItems, setWallet, wallet } from '../state/game.js';
import { storage } from '../state/storage.js';
import type { SheetProps } from '../ui/App.js';
import { Sheet } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { I, ICONS, type IconName } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { assetEntry } from '../assets/manifest.js';
import { oddsSumOk, pct } from './store-model.js';
import '../ui/store.css';
import { animate, EASE } from '../ui/motion.js';
import { burstAt, flyGrant, flyToBag } from '../ui/fx.js';

type Odds = ResponseOf<Api['getBoxOdds']>;
type OpenResult = ResponseOf<Api['openBoxes']>;
type Drop = OpenResult['drops'][number];

/** Названия источников шансов: ящики — по ID стопки, боссы — по справочнику. */
const BOX_KEYS: Record<string, string> = {
  'BOX-IRON': 'CL-BOX-IRON', 'BOX-MASTER': 'CL-STORE-G-BOX', 'BOX-UNCLE': 'CL-BOX-UNCLE', 'BOX-PASS': 'CL-BOX-PASS',
};
export function sourceName(id: string): string {
  if (BOX_KEYS[id]) return tr(BOX_KEYS[id]!);
  if (/^(B[0-9]{2,3}|W[0-9]{2})$/.test(id)) return bossInfo(id).name;
  return tr('CL-BOX-ANY');
}

const PENDING = (box: string) => `boxOpen:${box}`;

/** Картинка ящика: своя из манифеста, иначе «Железный ящик» (своего арта у «Ящика мастера» пока нет — дефект арта). */
export function boxArt(box: string): string {
  return assetEntry(box) || !assetEntry('BOX-IRON') ? box : 'BOX-IRON';
}
const RANK = { common: 0, good: 1, rare: 2, epic: 3, legendary: 4 } as const;

/** Лучшая редкость среди выпавшего — для haptics «выпала эпическая или легендарная» (ux/03 §8). */
export function bestRarity(drops: Drop[]): keyof typeof RANK | null {
  let best: keyof typeof RANK | null = null;
  for (const d of drops) if (d.kind === 'item' && (best === null || RANK[d.item.rarity] > RANK[best])) best = d.item.rarity;
  return best;
}

function DropCard(p: { d: Drop; i: number }) {
  const app = useApp();
  const d = p.d;
  const style = { animationDelay: `${p.i * 80}ms` };
  if (d.kind === 'item') {
    return (
      <button class={`item-tile drop rarity-${d.item.rarity}`} style={style} data-testid={`drop-${p.i}`} onClick={() => app.nav.openSheet('MOD-ITEM', { itemId: d.item.itemId })}>
        {d.trophy ? <span class="lvl">{tr('CL-BOX-TROPHY')}</span> : null}
        <span class="glyph"><Icon id={d.item.defId} fallback={ICONS[d.item.slot][1]} size={36} /></span>
        <span class="nm">{itemName(d.item.defId)}</span>
      </button>
    );
  }
  if (d.kind === 'currency') {
    return (
      <span class="item-tile drop" style={style} data-testid={`drop-${p.i}`}>
        <span class="glyph">{d.currency in ICONS ? <I n={d.currency as IconName} size={32} /> : CURRENCY[d.currency]?.icon}</span>
        <span class="nm">{amount(d.currency, d.amount)}</span>
      </span>
    );
  }
  return (
    <span class="item-tile drop" style={style} data-testid={`drop-${p.i}`}>
      <span class="glyph"><Icon id={boxArt(d.defId)} fallback="📦" size={32} /></span>
      <span class="nm">{sourceName(d.defId)} ×{d.qty}</span>
    </span>
  );
}

/** MOD-BOX-OPEN: закрытый ящик → «Открыть» → вскрытие 1.2 с (пропуск тапом) → выпавшее сеткой. */
export function BoxOpenSheet({ entry }: SheetProps) {
  const app = useApp();
  const box = String(entry.params['boxDefId'] ?? 'BOX-IRON');
  const [have, setHave] = useState(inventory.value?.stacks.find((s) => s.defId === box)?.qty ?? Number(entry.params['qty'] ?? 1));
  const keys = wallet.value?.keys ?? 0;
  const [phase, setPhase] = useState<'closed' | 'opening' | 'shown'>('closed');
  const [res, setRes] = useState<OpenResult | null>(null);
  const [noKey, setNoKey] = useState(false);
  const skip = useRef<() => void>(() => {});
  const bodyRef = useRef<HTMLDivElement>(null);
  const boxAt = useRef<{ x: number; y: number } | null>(null);
  const apply = (r: OpenResult) => {
    storage.remove(PENDING(box));
    setWallet(r.wallet);
    const items = r.drops.flatMap((d) => (d.kind === 'item' ? [d.item] : []));
    if (inventory.value) {
      inventory.value = { ...inventory.value, stacks: r.stacks };
      if (items.length) setInventoryItems((xs) => [...xs, ...items.filter((i) => !xs.some((x) => x.itemId === i.itemId))]);
      saveCache();
    }
    setHave(r.stacks.find((s) => s.defId === box)?.qty ?? 0);
    setRes(r);
  };
  const open = async (count: number, again?: { key: string }) => {
    const key = again?.key ?? app.idem();
    storage.setJson(PENDING(box), { key, count });
    const br = bodyRef.current?.querySelector('.box-pic')?.getBoundingClientRect();
    if (br) boxAt.current = { x: br.left + br.width / 2, y: br.top + br.height / 2 };
    setPhase('opening');
    app.haptic('heavy');
    const shown = new Promise<void>((resolve) => { const t = setTimeout(resolve, 1200); skip.current = () => { clearTimeout(t); resolve(); }; });
    try {
      const r = await app.call('openBoxes', { body: { boxDefId: box, count }, idempotencyKey: key });
      apply(r);
      await shown;
      setPhase('shown');
      const best = bestRarity(r.drops);
      if (best === 'epic' || best === 'legendary') app.haptic('success');
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      setPhase('closed');
      if (code === 'NETWORK' || code === 'TIMEOUT') { showToast(tr('CL-BOX-CUT'), 'error'); return; } // ключ остался — повтор вернёт то же
      storage.remove(PENDING(box));
      if (code === 'NOT_ENOUGH_KEYS') { setNoKey(true); app.haptic('error'); return; }
      void app.call('getWallet').then(setWallet).catch(() => {});
      app.handleError(e, { resource: tr('CL-BOX-SAFE') });
    }
  };
  // Обрыв на прошлом открытии: повторяем тот же запрос — сервер отдаст уже выданное (E05-T08).
  useEffect(() => {
    const p = storage.getJson<{ key: string; count: number }>(PENDING(box));
    if (p) void open(p.count, { key: p.key });
  }, []);
  // Вскрытие: вспышка цвета лучшей редкости из точки ящика, предметы вылетают из неё на свои места пружиной.
  useLayoutEffect(() => {
    if (phase !== 'shown' || !res) return;
    const body = bodyRef.current;
    const at = boxAt.current;
    if (!body || !at) return;
    burstAt(body, at, bestRarity(res.drops));
    body.querySelectorAll<HTMLElement>('.drop-grid > *').forEach((el, i) => {
      const r = el.getBoundingClientRect();
      const dx = at.x - (r.left + r.width / 2);
      const dy = at.y - (r.top + r.height / 2);
      animate(el, [
        { opacity: 0, transform: `translate(${dx}px, ${dy}px) scale(.25) rotate(${i % 2 ? -25 : 25}deg)` },
        { opacity: 1, transform: 'none' },
      ], { duration: 520, delay: 90 + Math.min(i, 10) * 70, easing: EASE.spring });
    });
  }, [phase]);
  // «Забрать»: валюты — монетами в шапку, предметы — в сумку; лист закрывается сразу, полёт — поверх.
  const collect = () => {
    const body = bodyRef.current;
    if (body && res) {
      res.drops.forEach((d, i) => {
        const tile = body.querySelector(`[data-testid="drop-${i}"]`);
        if (d.kind === 'currency') flyGrant(tile, { [d.currency]: d.amount });
      });
      flyToBag(body.querySelectorAll('.drop-grid > .item-tile.drop[class*="rarity-"] .glyph'));
    }
    app.nav.popSheet();
  };
  const many = Math.min(10, have);
  const actions = phase === 'shown'
    ? [{ text: tr('UI-BTN-COLLECT'), testId: 'box-ok', onClick: collect }]
    : phase === 'opening' ? []
      : noKey || (entry.params['needsKey'] && keys < 1)
        ? [{ text: tr('CL-BOX-NEED-KEY'), testId: 'box-need-key', onClick: () => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'keys' }) }]
        : [
          ...(many > 1 ? [{ text: tr('CL-BOX-OPEN-ALL', { n: many }), tone: 'outline' as const, testId: 'box-open-all', disabled: !online.value, onClick: () => open(many) }] : []),
          { text: online.value ? tr('CL-BOX-OPEN') : tr('UI-OFFLINE'), testId: 'box-open', disabled: !online.value || have < 1, onClick: () => open(1) },
        ];
  return (
    <Sheet entry={entry} title={<span class="row" style={{ gap: 8 }}><span style={{ flex: 1 }}>{sourceName(box)}</span><button class="odds-link" data-testid="box-odds" onClick={() => app.nav.openSheet('MOD-ODDS', { source: box })}>{tr('CL-STORE-ODDS')}</button></span>} actions={actions}>
      <div class="col box-body" ref={bodyRef} style={{ alignItems: 'center' }} data-testid="MOD-BOX-OPEN-body" data-phase={phase}>
        {phase !== 'shown' ? (
          <button class={`box-pic ${phase === 'opening' ? 'shake' : ''}`} aria-label={tr('CL-BOX-SKIP')} onClick={() => skip.current()}>
            <Icon id={boxArt(box)} fallback="📦" size={72} />
          </button>
        ) : null}
        {phase === 'closed' ? <span class="small muted" data-testid="box-have">{tr('CL-BOX-HAVE', { n: have })}{entry.params['needsKey'] ? ` · ${tr('CL-BOX-KEYS', { n: keys })}` : ''}</span> : null}
        {phase === 'opening' ? <span class="small muted">{tr('CL-BOX-OPENING')}</span> : null}
        {phase === 'shown' && res ? (
          <div class="drop-grid" data-testid="box-drops">{res.drops.map((d, i) => <DropCard key={i} d={d} i={i} />)}</div>
        ) : null}
        {phase === 'shown' && res ? <span class="small muted" data-testid="box-odds-version">{tr('CL-ODDS-VERSION', { v: res.oddsVersion })}</span> : null}
      </div>
    </Sheet>
  );
}

const ODDS_CACHE = (id: string) => `odds:${id}`;

/** MOD-ODDS: таблица шансов (редкости, предметы, трофеи отдельной строкой), версия, гарантия, «< 0,01%». */
export function OddsSheet({ entry }: SheetProps) {
  const app = useApp();
  const id = String(entry.params['source'] ?? 'BOX-IRON');
  const pity = entry.params['pityLeft'] as number | undefined;
  const cached = storage.getJson<{ at: number; odds: Odds }>(ODDS_CACHE(id));
  const [odds, setOdds] = useState<Odds | null>(cached?.odds ?? null);
  const [stale, setStale] = useState<number | null>(cached ? cached.at : null);
  const [err, setErr] = useState(false);
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    app.call('getBoxOdds', { params: { boxDefId: id } }).then((o) => {
      if (!alive) return;
      setOdds(o); setStale(null); setErr(false);
      storage.setJson(ODDS_CACHE(id), { at: Date.now(), odds: o });
    }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [n]);
  const items = odds?.items.filter((x) => !x.trophy) ?? [];
  const trophies = odds?.items.filter((x) => x.trophy) ?? [];
  return (
    <Sheet entry={entry} title={tr('ODDS-TITLE', { source: sourceName(id) })} actions={[{ text: tr('CL-REFUND-OK'), testId: 'odds-ok', onClick: () => app.nav.popSheet() }]}>
      <div class="col" data-testid="MOD-ODDS-body">
        <span class="small muted">{tr('ODDS-INTRO')}</span>
        {stale && err ? <div class="banner" data-testid="odds-stale">{tr('CL-ODDS-CACHED', { d: new Date(stale).toLocaleDateString('ru-RU') })}</div> : null}
        {!odds && err ? <div class="banner" role="alert">{tr('CL-ODDS-ERR')}<span class="spacer" /><button class="btn small outline" onClick={() => setN((x) => x + 1)}>{tr('CL-UI-KIT-15')}</button></div> : null}
        {!odds && !err ? <div class="skeleton" style={{ height: 160 }} /> : null}
        {odds ? (
          <>
            {odds.table.length ? (
              <table class="odds-table" data-testid="odds-rarity">
                <tbody>
                  {odds.table.map((r) => (
                    <tr key={r.rarity} class={`rarity-${r.rarity}`}><td><span class="odds-dot" />{RARITY_NAMES[r.rarity]}</td><td>{pct(r.chance)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : null}
            {items.length ? (
              <table class="odds-table" data-testid="odds-items">
                <tbody>{items.map((r) => <tr key={r.defId}><td>{oddsLabel(r)}</td><td>{pct(r.chance)}</td></tr>)}</tbody>
              </table>
            ) : null}
            {trophies.length ? (
              <>
                <b class="small">{tr('CL-ODDS-TROPHIES')}</b>
                <table class="odds-table" data-testid="odds-trophies">
                  <tbody>{trophies.map((r) => <tr key={r.defId}><td>{oddsLabel(r)}</td><td>{pct(r.chance)}</td></tr>)}</tbody>
                </table>
              </>
            ) : null}
            {odds.table.length && !oddsSumOk(odds.table) ? <span class="small neg">{tr('CL-ODDS-SUM')}</span> : null}
            {pity !== undefined ? <span class="small" data-testid="odds-pity">{tr('ODDS-COUNTER', { g: pity })}</span> : null}
            <span class="small muted">{tr('CL-ODDS-FOOTER')}</span>
            <span class="small muted" data-testid="odds-version">{tr('CL-ODDS-VERSION', { v: odds.oddsVersion })}</span>
          </>
        ) : null}
      </div>
    </Sheet>
  );
}

function oddsLabel(r: Odds['items'][number]): string {
  if (r.currency) return CURRENCY[r.currency]?.name ?? r.currency;
  if (r.defId.startsWith('EQ-')) return itemName(r.defId);
  return sourceName(r.defId);
}
